// components/account/KeyLibrarySection/index.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
  Toolbar,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../utils/context/authContext";
import { fetchUserKeysAPI, formatDate, SetFlag, UserGiftCardKey } from "utils";

type Order = "asc" | "desc";
type HeadKey = "timestamp" | "productName" | "code" | "amount" | "country";


// Best-effort ISO2 fallback if backend only sends country name
const nameToISO2: Record<string, string> = {
  france: "FR",
  belgique: "BE",
  mondial: "UN",
  global: "UN",
  "états-unis": "US",
  "etats-unis": "US",
  usa: "US",
  "royaume-uni": "GB",
};
const ensureISO2 = (code?: string, name?: string) =>
  code?.length === 2
    ? code.toUpperCase()
    : name
    ? nameToISO2[name.toLowerCase()]
    : undefined;

export const KeyLibrarySection: React.FC = () => {
  const { user } = useAuth();

  // data
  const [keys, setKeys] = useState<UserGiftCardKey[]>([]);
  const [total, setTotal] = useState(0);

  // server-side controls
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<HeadKey>("timestamp");
  const [page, setPage] = useState(0); // MUI 0-based
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // debounce search input
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const headCells: Array<{ id: HeadKey; label: string; sortable?: boolean }> =
    useMemo(
      () => [
        { id: "timestamp", label: "Date", sortable: true },
        { id: "productName", label: "Produit", sortable: true },
        { id: "code", label: "Code", sortable: true },
        { id: "amount", label: "Prix", sortable: true },
        { id: "country", label: "Pays", sortable: true },
      ],
      []
    );

  const handleRequestSort = (property: HeadKey) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // fetch whenever controls/user change
  useEffect(() => {
    (async () => {
      if (!user?.userID) return;
      try {
        const res = await fetchUserKeysAPI(user.userID, {
          page: page + 1, // backend is 1-based
          limit: rowsPerPage,
          search,
          sortBy: orderBy,
          order,
        });
        console.log("fetchUserKeysAPI result (normalized):", res); // { keys, total, page, limit }
        setKeys(res.keys || []);
        setTotal(res.total || 0);
      } catch (e) {
        console.error("Erreur chargement clés:", e);
        setKeys([]);
        setTotal(0);
      }
    })();
  }, [user, page, rowsPerPage, search, order, orderBy]);

  return (
    <Box p={3} sx={{ width: "100%", padding: 0 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Bibliothèque de clés
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Toolbar sx={{ pl: 0, pr: 0 }}>
        <TextField
          size="small"
          placeholder="Rechercher (code, produit, pays)..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 360, maxWidth: "100%" }}
        />
      </Toolbar>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {headCells.map((col) => (
                <TableCell
                  key={col.id}
                  sortDirection={orderBy === col.id ? order : false}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : "asc"}
                      onClick={() => handleRequestSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {keys.map((k) => {
              const cc = ensureISO2(k.countryCode, k.country);
              return (
                <TableRow key={k.id} hover>
                  <TableCell>{formatDate(k.timestamp)}</TableCell>

                  <TableCell>
                    {k.productName || "Produit non attribué"}
                  </TableCell>

                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    {(k.code || "").trim() || "—"}
                  </TableCell>

                  <TableCell>{k.amount} BSB Coins</TableCell>

                  <TableCell>
                    <SetFlag
                      countryName={k.country || ""}
                      countryCode={cc || ""}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};
